import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import React from "react";

export type InlineAlertVariant = "error" | "success" | "warning" | "info";

const VARIANT: Record<InlineAlertVariant, { wrap: string; icon: typeof Info }> =
  {
    error: {
      wrap: "border-[#EF4444] bg-[#FEE2E2] text-[#B91C1C]",
      icon: AlertTriangle,
    },
    success: {
      wrap: "border-[#22C55E] bg-[#DCFCE7] text-[#166534]",
      icon: CheckCircle2,
    },
    warning: {
      wrap: "border-[#F59E0B] bg-[#FEF3C7] text-[#92400E]",
      icon: AlertTriangle,
    },
    info: {
      wrap: "border-[#6366F1] bg-[#EEF2FF] text-[#4338CA]",
      icon: Info,
    },
  };

interface InlineAlertProps {
  variant: InlineAlertVariant;
  children: React.ReactNode;
  /** When provided, renders a dismiss (×) button that calls this. */
  onDismiss?: () => void;
  className?: string;
}

/**
 * Reusable inline feedback banner (error / success / warning / info). Chunky,
 * rounded, bold to match the app. Errors announce assertively; the rest are
 * polite status regions. Pass `onDismiss` to make it dismissible.
 */
export const InlineAlert: React.FC<InlineAlertProps> = ({
  variant,
  children,
  onDismiss,
  className = "",
}) => {
  const { wrap, icon: Icon } = VARIANT[variant];
  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={`flex items-start gap-3 rounded-2xl border-2 p-3 text-sm font-extrabold ${wrap} ${className}`}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 leading-relaxed">{children}</div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-lg p-1 opacity-70 transition-opacity hover:opacity-100"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
